/*
 * Find the differences between two objects and push to a new object
 * (c) 2019 Chris Ferdinandi & Jascha Brinkmann, MIT License, https://gomakethings.com & https://twitter.com/jaschaio
 *
 */

/* Few edits for our use case by Kaine Bruce 12/04/2023 */

/**
 * @param  {Object} obj1 The original object
 * @param  {Object} obj2 The object to compare against it
 * @return {Object}      An object of differences between the two
 */
export function difference(obj1, obj2) {
  // Make sure an object to compare is provided
  if (!obj2 || Object.prototype.toString.call(obj2) !== '[object Object]') {
    return obj1;
  }

  //
  // Variables
  //

  var diffs = {};
  var key;

  //
  // Methods
  //

  /**
   * Check if two arrays are equal
   * @param  {Array}   arr1 The first array
   * @param  {Array}   arr2 The second array
   * @return {Boolean}      If true, both arrays are equal
   */
  var arraysMatch = function (arr1, arr2) {
    // Check if the arrays are the same length
    if (arr1.length !== arr2.length) {
      return false;
    }
    // We just need to see if these are the same when converted to as strings, not the object reference.

    const arr1String = JSON.stringify(arr1);
    const arr2String = JSON.stringify(arr2);
    if (arr1String === arr2String) return true;
    return false;

    // Check if all items exist and are in the same order
    for (var i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        console.log('arrays do not match');
        return false;
      }
    }

    // Otherwise, return true
    console.log('arrays match');
    return true;
  };

  /**
   * Compare two items and push non-matches to object
   * @param  {*}      item1 The first item
   * @param  {*}      item2 The second item
   * @param  {String} key   The key in our object
   */
  var compare = function (item1, item2, key) {
    // Get the object type
    var type1 = Object.prototype.toString.call(item1);
    var type2 = Object.prototype.toString.call(item2);

    // If type2 is undefined it has been removed
    if (type2 === '[object Undefined]') {
      diffs[key] = null;
      return;
    }

    // If items are different types
    if (type1 !== type2) {
      diffs[key] = item2;
      return;
    }

    // If an object, compare recursively
    // if (type1 === "[object Object]") {

    //   var objDiff = diff(item1, item2);

    //   if (Object.keys(objDiff).length > 0) {
    //     diffs[key] = objDiff;
    //   }
    //   return;
    // }

    // KB - If an object, we don't compare recursively
    if (type1 === '[object Object]') {
      if (JSON.stringify(item1) !== JSON.stringify(item2)) {
        diffs[key] = item2;
      }
      return;
    }

    // If an array, compare
    if (type1 === '[object Array]') {
      if (!arraysMatch(item1, item2)) {
        diffs[key] = item2;
      }
      return;
    }

    // Else if it's a function, convert to a string and compare
    // Otherwise, just compare
    if (type1 === '[object Function]') {
      if (item1.toString() !== item2.toString()) {
        diffs[key] = item2;
      }
    } else {
      if (item1 !== item2) {
        diffs[key] = item2;
      }
    }
  };

  //
  // Compare our objects
  //

  // Loop through the first object
  for (key in obj1) {
    if (obj1.hasOwnProperty(key)) {
      compare(obj1[key], obj2[key], key);
    }
  }

  // Loop through the second object and find missing items
  for (key in obj2) {
    if (obj2.hasOwnProperty(key)) {
      if (!obj1[key] && obj1[key] !== obj2[key]) {
        diffs[key] = obj2[key];
      }
    }
  }

  // Return the object of differences
  return diffs;
}

/**
 * uses JSON.parse(JSON.stringify()) to deep copy the passed diff
 * Removes subkeys generated by the difference function that we don't want to save
 *
 * Only keeps the keys 'value' and 'type'
 * Also removes top level keys that have a value as null
 * @param  {Object}   newDiff The first array
 * @return {Object}
 */
export function cleanDiff(diffIn) {
  // We only want to save certain keys

  const diffCopy = JSON.parse(JSON.stringify(diffIn));

  recursivelyCleanDiff(diffCopy);

  // console.log('diffCopy', diffCopy);
  return diffCopy;
}

function recursivelyCleanDiff(diffCopy) {
  // removeMe is used further up the chain to remove the key from the object, but we can't remove this here.
  const keysToKeep = ['value', 'type', 'removeMe', 'port_index', 'scene_data'];

  for (const [key, value] of Object.entries(diffCopy)) {
    // Removes top level null values
    if (value === null) {
      delete diffCopy[key];
      continue;
    }

    for (const subKey in diffCopy[key]) {
      if (!keysToKeep.includes(subKey)) {
        delete value[subKey];
      }
    }

    if ('scene_data' in value) {
      recursivelyCleanDiff(value['scene_data']);
    }
  }

  return diffCopy;
}
